import React, { useEffect, useState } from "react";
import {
  getPasswordRuleChecks,
  validateEmailDetailed,
  validateRegistrationPassword,
} from "../utils/validation";
import PasswordRuleHints from "./PasswordRuleHints";
import "../style/Login.css";
import logo from "../assets/logo.png";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useAuth } from "../auth/AuthProvider";
import { signInWithGooglePopup } from "../lib/firebase";
import { authRegister, authGoogleLogin } from "../services/api";
import { handleRoleBasedRedirect } from "../utils/roleRouting";
import Footer from "../components/Footer";

type Mode = "login" | "register";

export default function AuthCard() {
  const [mode, setMode] = useState<Mode>("login");
  const [form, setForm] = useState({
    firstName: "",
    lastName: "",
    email: "",
    organisation: "",
    username: "",
    roleDescription: "",
    usagePurpose: "",
    password: "",
    confirm: "",
    acceptTandC: false,
    newsLetterSubs: false,
  });

  const { setUser, login, user } = useAuth();
  const [show, setShow] = useState(false);
  const [error, setError] = useState("");
  const [warningDismissed, setWarningDismissed] = useState(false);
  const [success, setSuccess] = useState(false);
  const [showIamInfo, setShowIamInfo] = useState(false);
  const [showPasswordRules, setShowPasswordRules] = useState(false);
  const [loading, setLoading] = useState(false);

  const nav = useNavigate();
  const [searchParams] = useSearchParams();
  const redirectAfterAuth = searchParams.get("redirect")
    ? `/${searchParams.get("redirect")}`
    : "/";
  const urlMessage = searchParams.get("message");

  useEffect(() => {
    if (urlMessage) {
      setError(urlMessage);
      setWarningDismissed(false);
    }
  }, [urlMessage]);

  function setAuthError(message: string) {
    setError(message);
    setWarningDismissed(false);
  }

  function isInvalidCredentialError(message: string) {
    const normalized = message.toLowerCase();
    return (
      normalized.includes("incorrect email") ||
      normalized.includes("incorrect password") ||
      normalized.includes("invalid email") ||
      normalized.includes("invalid password") ||
      normalized.includes("wrong email") ||
      normalized.includes("wrong password") ||
      normalized.includes("invalid credentials")
    );
  }

  function handleChange(e: React.ChangeEvent<HTMLInputElement>) {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
  }

  function validateForm() {
    const emailErr = validateEmailDetailed(form.email.trim());
    if (emailErr) return emailErr;

    if (mode === "login") {
      if (!form.password) return "Password is required.";
    } else {
      const passErr = validateRegistrationPassword(form.password);
      if (passErr) return passErr;
    }

    if (mode === "register") {
      if (!form.firstName.trim()) return "Please enter your First name.";
      if (!form.lastName.trim()) return "Please enter your Last name.";
      if (!form.organisation.trim()) return "Please enter your Organisation name.";
      if (!form.username.trim()) return "Please choose a username.";
      if (!form.roleDescription.trim()) return "Please describe your role.";
      if (!form.usagePurpose.trim()) {
        return "Please describe how you plan to use the Impact Alignment Map.";
      }
      if (!form.acceptTandC) {
        return "You must accept the Terms & Conditions and Privacy Policy.";
      }
      if (!form.newsLetterSubs) return "Please subscribe to our newsletter.";
      if (form.confirm !== form.password) return "Passwords do not match.";
    }

    return "";
  }

  async function submitRegister() {
    const {
      email,
      password,
      firstName,
      lastName,
      organisation,
      username,
      acceptTandC,
      newsLetterSubs,
      roleDescription,
      usagePurpose,
    } = form;

    const { token, user: createdUser } = await authRegister({
      email: email.trim(),
      password,
      firstName,
      lastName,
      organisation,
      username,
      acceptTandC,
      newsLetterSubs,
      roleDescription: roleDescription.trim(),
      usagePurpose: usagePurpose.trim(),
    });

    localStorage.setItem("token", token);
    localStorage.setItem("user", JSON.stringify(createdUser));
    setUser(createdUser as any);
    handleRoleBasedRedirect(createdUser as any, nav);
  }

  async function submitLogin() {
    await login(form.email.trim(), form.password);

    setTimeout(() => {
      try {
        const raw = localStorage.getItem("user");
        const u = raw ? JSON.parse(raw) : user;
        handleRoleBasedRedirect(u as any, nav);
      } catch {
        nav(redirectAfterAuth, { replace: true });
      }
    }, 0);
  }

  const registerPasswordOk =
    mode !== "register" || getPasswordRuleChecks(form.password).allMet;

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();

    const err = validateForm();
    if (err) {
      setAuthError(err);
      setSuccess(false);
      return;
    }

    setError("");
    setSuccess(false);
    setLoading(true);

    try {
      if (mode === "register") {
        await submitRegister();
        setSuccess(true);
      } else {
        await submitLogin();
        setSuccess(true);
      }
    } catch (e: any) {
      const msg =
        e?.response?.data?.error?.message ||
        e?.response?.data?.error ||
        e?.response?.data?.message ||
        e?.message ||
        "Something went wrong. Please try again.";
      setAuthError(typeof msg === "string" ? msg : JSON.stringify(msg));
      setSuccess(false);
    } finally {
      setLoading(false);
    }
  }

  function switchMode(next: Mode) {
    if (next === mode) return;
    setMode(next);
    setShow(false);
    setShowPasswordRules(false);
    setError("");
    setSuccess(false);
    setForm((f) => ({
      ...f,
      password: "",
      confirm: "",
      firstName: next === "register" ? f.firstName : "",
      lastName: next === "register" ? f.lastName : "",
      organisation: next === "register" ? f.organisation : "",
      username: next === "register" ? f.username : "",
      roleDescription: next === "register" ? f.roleDescription : "",
      usagePurpose: next === "register" ? f.usagePurpose : "",
      acceptTandC: next === "register" ? f.acceptTandC : false,
      newsLetterSubs: next === "register" ? f.newsLetterSubs : false,
    }));
  }

  async function handleGoogle(e: React.MouseEvent<HTMLButtonElement>) {
    e.preventDefault();
    try {
      setLoading(true);
      setError("");
      
      // Get Google ID token
      const { idToken } = await signInWithGooglePopup();
      
      // Authenticate with backend
      const { token, user } = await authGoogleLogin(idToken);

      // Store authentication data
      localStorage.setItem("token", token);
      localStorage.setItem("user", JSON.stringify(user));
      setUser(user as any);

      // Use role-based redirect logic
      handleRoleBasedRedirect(user, nav);
      
      setSuccess(true);
    } catch (err: any) {
      const code = err?.code;
      let message = err?.message || "Google sign-in failed. Please try again.";
      if (code === "ACCOUNT_NOT_FOUND" || err?.status === 404) {
        switchMode("register");
        message =
          "User account not created. Please create an account first, then sign in.";
      } else
      if (code === "auth/unauthorized-domain") {
        message = "Firebase unauthorized domain. Add localhost:3000 and localhost:3004 in Firebase console → Authentication → Settings → Authorized domains.";
      } else if (code === "auth/popup-blocked") {
        message = "Popup blocked. Allow popups for this site and try again.";
      } else if (code === "auth/popup-closed-by-user") {
        message = "Popup closed before completing sign-in. Please try again.";
      } else if (code === "auth/network-request-failed") {
        message = "Network error during Google sign-in. Check connectivity and disable blockers.";
      }
      setError(message);
      setSuccess(false);
    } finally {
      setLoading(false);
    }
  }

  return (
    <div className="login-page">
      <div className="login-page__content">
        <div className="login-container">
          <div className="login-left">
            <form
              onSubmit={handleSubmit}
              className={`login-card ${
                mode === "login" ? "login-mode" : "register-mode"
              }`}
            >
              <div className="login-logo">
                <img src={logo} alt="App Logo" />
              </div>

              <div className="auth-toggle" role="tablist" aria-label="Auth mode">
                <button
                  type="button"
                  className={`auth-tab ${mode === "login" ? "active" : ""}`}
                  onClick={() => switchMode("login")}
                  role="tab"
                  aria-selected={mode === "login"}
                >
                  Sign In
                </button>
                <button
                  type="button"
                  className={`auth-tab ${mode === "register" ? "active" : ""}`}
                  onClick={() => switchMode("register")}
                  role="tab"
                  aria-selected={mode === "register"}
                >
                  Create Account
                </button>
              </div>

              <h1 className="login-title">
                {mode === "login" ? "Sign In" : "Create your account"}
              </h1>

              {mode === "login" &&
                error &&
                isInvalidCredentialError(error) &&
                !warningDismissed && (
                  <div
                    className="login-warning-popup"
                    role="alert"
                    aria-live="polite"
                  >
                    <div className="login-warning-icon" aria-hidden="true">
                      &#9888;
                    </div>
                    <div className="login-warning-text">
                      <strong>Incorrect email or password</strong>
                      <span>
                        If you cannot remember your password, click on the link
                        below for resetting your password.
                      </span>
                    </div>
                    <button
                      type="button"
                      className="login-warning-close"
                      aria-label="Close warning"
                      onClick={() => setWarningDismissed(true)}
                    >
                      &times;
                    </button>
                  </div>
                )}

              {mode === "register" && (
                <>
                  <h2 className="register-section-title">Personal Info</h2>
                  <div className="register-grid register-grid--two-col">
                    <div className="register-field">
                      <label htmlFor="firstName" className="formRow">
                        First Name <span className="form-required-star">*</span>
                      </label>
                      <input
                        id="firstName"
                        name="firstName"
                        type="text"
                        value={form.firstName}
                        onChange={handleChange}
                        placeholder="Your First name"
                        required
                      />
                    </div>
                    <div className="register-field">
                      <label htmlFor="lastName" className="formRow">
                        Last Name <span className="form-required-star">*</span>
                      </label>
                      <input
                        id="lastName"
                        name="lastName"
                        type="text"
                        value={form.lastName}
                        onChange={handleChange}
                        placeholder="Your Last name"
                        required
                      />
                    </div>
                  </div>
                  <div className="register-field register-field--full">
                    <label htmlFor="email" className="formRow">
                      Email Address <span className="form-required-star">*</span>
                    </label>
                    <input
                      id="email"
                      name="email"
                      type="email"
                      autoComplete="email"
                      value={form.email}
                      onChange={handleChange}
                      placeholder="you@domain.com"
                      required
                    />
                  </div>
                </>
              )}

              {mode === "login" && (
                <>
                  <div className="register-field register-field--full">
                    <label htmlFor="email" className="formRow">
                      Email Address <span className="form-required-star">*</span>
                    </label>
                    <input
                      id="email"
                      name="email"
                      type="email"
                      autoComplete="email"
                      value={form.email}
                      onChange={handleChange}
                      placeholder="you@domain.com"
                      required
                    />
                  </div>
                  <div className="register-field register-field--full">
                    <label htmlFor="password" className="formRow">
                      Password <span className="form-required-star">*</span>
                    </label>
                    <input
                      id="password"
                      name="password"
                      className="pw-input"
                      type={show ? "text" : "password"}
                      autoComplete="current-password"
                      value={form.password}
                      onChange={handleChange}
                      placeholder="********"
                      required
                    />
                  </div>
                </>
              )}

              {mode === "register" && (
                <>
                  <h2 className="register-section-title">Organisation</h2>
                  <div className="register-grid register-grid--two-col">
                    <div className="register-field">
                      <label htmlFor="organisation" className="formRow">
                        Organisation <span className="form-required-star">*</span>
                      </label>
                      <input
                        id="organisation"
                        name="organisation"
                        type="text"
                        value={form.organisation}
                        onChange={handleChange}
                        placeholder="Quality for Outcomes"
                        required
                      />
                    </div>
                    <div className="register-field">
                      <label htmlFor="username" className="formRow">
                        Username <span className="form-required-star">*</span>
                      </label>
                      <input
                        id="username"
                        name="username"
                        type="text"
                        value={form.username}
                        onChange={handleChange}
                        placeholder="Choose a username"
                        required
                      />
                    </div>
                  </div>
                  <div className="register-field register-field--full">
                    <label htmlFor="roleDescription" className="formRow">
                      What is your role?{" "}
                      <span className="form-required-star">*</span>
                    </label>
                    <textarea
                      id="roleDescription"
                      name="roleDescription"
                      className="register-textarea"
                      value={form.roleDescription}
                      onChange={(e) =>
                        setForm((prev) => ({
                          ...prev,
                          roleDescription: e.target.value,
                        }))
                      }
                      placeholder="Describe your role"
                      required
                    />
                  </div>
                  <div className="register-field register-field--full">
                    <label htmlFor="usagePurpose" className="formRow">
                      How do you plan to use the Impact Alignment Map?{" "}
                      <span className="form-required-star">*</span>
                    </label>
                    <textarea
                      id="usagePurpose"
                      name="usagePurpose"
                      className="register-textarea"
                      value={form.usagePurpose}
                      onChange={(e) =>
                        setForm((prev) => ({
                          ...prev,
                          usagePurpose: e.target.value,
                        }))
                      }
                      placeholder="Describe how you plan to use this tool"
                      required
                    />
                  </div>

                  <h2 className="register-section-title">Security</h2>
                  <div className="register-field register-field--full">
                    <div className="register-grid register-grid--two-col">
                      <div className="register-field">
                        <label htmlFor="password" className="formRow">
                          Password{" "}
                          <span className="form-required-star">*</span>
                        </label>
                        <input
                          id="password"
                          name="password"
                          className="pw-input"
                          type={show ? "text" : "password"}
                          autoComplete="new-password"
                          value={form.password}
                          onChange={handleChange}
                          onFocus={() => setShowPasswordRules(true)}
                          onBlur={() => setShowPasswordRules(false)}
                          placeholder="********"
                          required
                        />
                      </div>
                      <div className="register-field">
                        <label htmlFor="confirm" className="formRow">
                          Confirm Password{" "}
                          <span className="form-required-star">*</span>
                        </label>
                        <input
                          id="confirm"
                          name="confirm"
                          type={show ? "text" : "password"}
                          value={form.confirm}
                          onChange={handleChange}
                          placeholder="Re-enter password"
                          required
                        />
                      </div>
                    </div>
                    {showPasswordRules && (
                      <PasswordRuleHints password={form.password} />
                    )}
                  </div>
                </>
              )}

              <div className="bottom-links">
                {mode === "login" && (
                  <div className="forgot-password">
                    <a href="/forgot-password" className="forgot-password">
                      Forgot Password?
                    </a>
                  </div>
                )}

                <div className="show-password">
                  <label>
                    <input
                      type="checkbox"
                      checked={show}
                      onChange={(e) => setShow(e.target.checked)}
                      aria-controls="password"
                    />
                    Show password
                  </label>
                </div>
              </div>

              {mode === "login" && (
                <div className="social">
                  <p>or</p>
                  <div className="social-buttons">
                    <button
                      type="button"
                      className="googlebtn"
                      onClick={handleGoogle}
                      disabled={loading}
                    >
                      <img
                        src="https://img.icons8.com/color/18/000000/google-logo.png"
                        alt="Google"
                      />
                      <span>Continue with Google</span>
                    </button>
                  </div>
                </div>
              )}

              {mode === "register" && (
                <>
                  <label
                    className="terms register-option"
                    style={{
                      display: "flex",
                      gap: 8,
                      alignItems: "flex-start",
                      marginBottom: "6px",
                    }}
                  >
                    <input
                      type="checkbox"
                      name="acceptTandC"
                      checked={form.acceptTandC}
                      onChange={(e) =>
                        setForm((p) => ({
                          ...p,
                          acceptTandC: e.target.checked,
                        }))
                      }
                      required
                      style={{ marginTop: "2px", flexShrink: 0 }}
                    />
                    <span style={{ lineHeight: "1.4" }}>
                      I accept the{" "}
                      <a
                        href="/terms"
                        target="_blank"
                        rel="noreferrer"
                        className="legal-link"
                        onClick={(e) => {
                          e.preventDefault();
                          window.open("/terms", "_blank");
                        }}
                      >
                        Terms &amp; Conditions and Privacy Policy
                      </a>{" "}
                      and acknowledge that I have read and understood them.{" "}
                      <span className="form-required-star">*</span>
                    </span>
                  </label>

                  <label
                    className="terms register-option"
                    style={{
                      display: "flex",
                      gap: 8,
                      alignItems: "center",
                      marginBottom: "6px",
                    }}
                  >
                    <input
                      type="checkbox"
                      name="newsLetterSubs"
                      checked={form.newsLetterSubs}
                      onChange={(e) =>
                        setForm((p) => ({
                          ...p,
                          newsLetterSubs: e.target.checked,
                        }))
                      }
                      required
                    />
                    Subscribe to our newsletter{" "}
                    <span className="form-required-star">*</span>
                  </label>
                </>
              )}

              <button
                type="submit"
                className="submit-btn"
                disabled={loading || !registerPasswordOk}
                title={
                  mode === "register" && !registerPasswordOk
                    ? "Complete all password rules to continue"
                    : undefined
                }
              >
                {loading
                  ? mode === "login"
                    ? "Signing in..."
                    : "Creating..."
                  : mode === "login"
                    ? "Sign In"
                    : "Create Account"}
              </button>

              {mode === "register" && (
                <p className="register-switch">
                  Already have an account?{" "}
                  <button
                    type="button"
                    className="register-switch-btn"
                    onClick={() => switchMode("login")}
                  >
                    Sign In
                  </button>
                </p>
              )}

              {error &&
                !(
                  mode === "login" &&
                  isInvalidCredentialError(error) &&
                  !warningDismissed
                ) && <p className="error">{error}</p>}
              {success && (
                <p className="success">
                  {mode === "login" ? "Logged in!" : "Account created!"}
                </p>
              )}
            </form>
          </div>

          <div className="login-hero">
            <div className="hero-cloud">
              <div className="hero-cloud-side hero-cloud-side--form" />
              <div className="hero-cloud-side hero-cloud-side--welcome" />
            </div>
            <div className="hero-copy">
              <h2 className="hero-welcome-title">Welcome!</h2>
              <div className="hero-iam-heading-row">
                <h3 className="hero-iam-heading">
                  Impact Alignment Map<span className="hero-tm">TM</span>
                </h3>
                <button
                  type="button"
                  className="hero-iam-info-btn"
                  aria-label="About Impact Alignment Map"
                  onClick={() => setShowIamInfo(true)}
                >
                  i
                </button>
              </div>
              <p className="hero-tagline">
                Strong programs. Real impact. Smart funding.
              </p>
            </div>
          </div>
        </div>

        {showIamInfo && (
          <div
            className="hero-info-backdrop"
            role="dialog"
            aria-modal="true"
            aria-labelledby="iam-info-title"
          >
            <div className="hero-info-dialog">
              <button
                type="button"
                className="hero-info-close"
                aria-label="Close information"
                onClick={() => setShowIamInfo(false)}
              >
                &times;
              </button>
              <h3 id="iam-info-title" className="hero-info-dialog-title">
                Impact Alignment Map
              </h3>
              <div className="hero-info-body">
                <p>
                  This tool helps you create a clear, visual representation of
                  how your program is expected to lead to impact. It builds on
                  familiar approaches such as logic models and theories of
                  change, but mainly focuses on helping you clearly demonstrate
                  how your program contributes to meaningful, longer term impact.
                </p>
                <p>You can use this tool to support:</p>
                <ul>
                  <li>
                    program planning (clarifying what you are trying to achieve
                    and how)
                  </li>
                  <li>evaluation (identifying what to measure and why)</li>
                  <li>
                    reporting and communication (showing how your work leads to
                    impact)
                  </li>
                </ul>
              </div>
            </div>
          </div>
        )}
      </div>

      <Footer />
    </div>
  );
}